import type { EmployerVerificationDetail } from '../../api/employerVerificationDashboardApi';
import type { GateKey } from '../employerVerificationCopy';
import {
  gateDisplayBucketFromStatus,
  resolvedCheckpointDisplayStatus,
} from '../employerVerificationGateAudit';
import { buildAiDigitalFootprintEvidenceView } from './aiDigitalFootprintPresentation';
import type { ReviewReason } from './reviewReason.types';

function parseRatingFromReason(reason: string): { rating: string | null; reviews: string | null } {
  const rating = reason.match(/shows\s+(\d+(?:\.\d+)?)\/5/i)?.[1] ?? null;
  const reviews = reason.match(/from\s+(\d+)\s+reviews/i)?.[1] ?? null;
  return { rating, reviews };
}

export const LIMITED_DIGITAL_PRESENCE_FINDING_ID = 'limited-digital-presence';

export type DigitalPresenceBriefContent = {
  caseSummary: string;
  caseSummaryPoints: string[];
  reviewMeasures: string;
  reviewMeasuresPoints: string[];
};

type CheckpointSnap = {
  step: string;
  bucket: string;
  reason: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function isIssueBucket(bucket: string): boolean {
  const u = bucket.trim().toLowerCase();
  return u === 'fail' || u === 'review';
}

function readLegitimacyCheckpoints(
  finding: ReviewReason,
  detail: EmployerVerificationDetail | null | undefined,
): CheckpointSnap[] {
  if (!detail?.gateCheckpointAudit) return [];
  const out: CheckpointSnap[] = [];

  for (const check of finding.sourceChecks) {
    if (check.gate.trim().toUpperCase() !== 'LEGITIMACY') continue;
    const auditSteps = detail.gateCheckpointAudit[check.gate as GateKey];
    const cp = auditSteps?.find((s) => s.step.trim().toUpperCase() === check.step.trim().toUpperCase());
    if (!cp) continue;
    const bucket = gateDisplayBucketFromStatus(resolvedCheckpointDisplayStatus(cp));
    out.push({
      step: check.step.trim().toUpperCase(),
      bucket,
      reason: cp.reason ?? '',
    });
  }
  return out;
}

function snap(checkpoints: CheckpointSnap[], step: string): CheckpointSnap | null {
  return checkpoints.find((c) => c.step === step) ?? null;
}

function parseOperationalStatus(reason: string): string | null {
  return reason.match(/Google Places status is\s+([A-Z_]+)/i)?.[1]?.trim().toUpperCase() ?? null;
}

function isPublicSectorLabel(sector: string | null): boolean {
  if (!sector) return false;
  return /public|crown|government|municipal|federal|provincial|agency|utility|hydro|police|school board|NAICS\s*91/i.test(
    sector,
  );
}

function googlePlacesWeak(snap: CheckpointSnap | null): boolean {
  if (!snap || !isIssueBucket(snap.bucket)) return false;
  const r = snap.reason;
  if (/no Google Places footprint|no rating and 0 reviews|below pass thresholds|below the minimum/i.test(r)) {
    return true;
  }
  const { rating, reviews } = parseRatingFromReason(r);
  return Boolean(rating || reviews);
}

function readAiSignals(detail: EmployerVerificationDetail | null | undefined) {
  const evidence = asRecord(detail?.evidence);
  const aiRaw = asRecord(evidence?.aiDigitalFootprint) ?? evidence;
  const aiView = buildAiDigitalFootprintEvidenceView({
    evidence,
    reason: '',
    bucket: 'review',
    declaredEmployer: readString(detail?.employer?.legalName),
  });

  const website =
    readString(aiRaw?.officialWebsite) ??
    aiView.collectedSignals.find((row) => row.label === 'Official website')?.value ??
    null;
  const sector = readString(aiRaw?.sector) ?? aiView.collectedSignals.find((row) => row.label === 'Sector')?.value ?? null;
  const corpId =
    readString(aiRaw?.corporationId) ??
    aiView.collectedSignals.find((row) => row.label === 'Corporation ID')?.value ??
    null;
  const businessNumber =
    readString(aiRaw?.businessNumber) ??
    aiView.collectedSignals.find((row) => row.label === 'Business number')?.value ??
    null;
  const detected =
    readString(aiRaw?.detectedEmployer) ??
    aiView.primaryDataPoints.find((row) => row.label === 'AI match')?.value ??
    null;
  const confidence =
    aiView.primaryDataPoints.find((row) => row.label === 'Confidence')?.value ?? null;
  const realCompany = aiRaw?.isRealCompany === true;

  const identityCount = [website, corpId, businessNumber].filter(Boolean).length;
  const aiRich = Boolean(detected && (realCompany || identityCount >= 1 || confidence));

  return {
    detected,
    confidence,
    website,
    sector,
    corpId,
    businessNumber,
    publicSector: isPublicSectorLabel(sector),
    aiRich,
    identityCount,
    aiView,
  };
}

type AiSignals = ReturnType<typeof readAiSignals>;

function buildAiEvidenceBullets(ai: AiSignals, maxLines = 4): string[] {
  const lines: string[] = [];
  if (ai.detected) {
    const conf = ai.confidence ? ` (${ai.confidence} confidence)` : '';
    lines.push(`EvolveOS matched ${ai.detected}${conf}.`);
  }
  if (ai.website) lines.push('Official employer website is on file.');
  if (ai.corpId || ai.businessNumber) lines.push('Registry identifiers were collected.');
  if (ai.sector) lines.push(`Sector: ${ai.sector}.`);
  if (!lines.length && ai.aiRich) lines.push('AI digital footprint analysis found employer evidence.');
  return lines.slice(0, maxLines);
}

/** Contextual bullets for “limited online presence” — separates weak Maps from missing employer proof. */
export function buildDigitalPresenceFindingContent(
  finding: ReviewReason,
  detail: EmployerVerificationDetail | null | undefined,
): DigitalPresenceBriefContent {
  const checkpoints = readLegitimacyCheckpoints(finding, detail);
  const ai = readAiSignals(detail);
  const googleSnap = snap(checkpoints, 'DIGITAL_FOOTPRINT');
  const operationalSnap = snap(checkpoints, 'OPERATIONAL_STATUS');

  const googleWeak = googlePlacesWeak(googleSnap);
  const operationalStatus = operationalSnap ? parseOperationalStatus(operationalSnap.reason) : null;
  const operationalIssue =
    operationalSnap != null &&
    isIssueBucket(operationalSnap.bucket) &&
    operationalStatus != null &&
    operationalStatus !== 'OPERATIONAL';
  // Office closed / moved — strongest site signal
  if (operationalIssue) {
    const statusLabel = operationalStatus?.replace(/_/g, ' ').toLowerCase() ?? 'not active';
    const caseSummaryPoints = [
      `Google Places reports this location as ${statusLabel} — the office may have shut down or moved.`,
      ...(ai.aiRich
        ? buildAiEvidenceBullets(ai, 2).map((line) => line.replace(/^EvolveOS matched/, 'EvolveOS still matched'))
        : ['Limited corroborating identity signals were collected beyond Maps status.']),
      'Confirm with the applicant whether they work onsite, at a new address, or remotely.',
    ];
    return {
      caseSummary: 'Worksite may no longer be active at the listed address.',
      caseSummaryPoints,
      reviewMeasures: 'Confirm current workplace before approving employment.',
      reviewMeasuresPoints: [
        'Ask whether the employer relocated, closed this branch, or the role is remote.',
        'Compare registry, AI footprint, and Street View against the declared worksite.',
        'Do not treat low Google reviews alone as proof the employer is fake when other signals are strong.',
      ],
    };
  }

  if (ai.aiRich && googleWeak) {
    const caseSummaryPoints = [
      ...buildAiEvidenceBullets(ai),
      'Google Places rating or review count is low — that alone does not mean the employer is fake.',
      ai.publicSector
        ? 'Public-sector and crown employers often have thin Google review profiles.'
        : 'Many legitimate employers have limited Maps visibility.',
    ];
    return {
      caseSummary: 'Maps visibility is weak, but other public evidence supports the employer.',
      caseSummaryPoints,
      reviewMeasures: 'Weigh AI and registry signals ahead of Google star ratings.',
      reviewMeasuresPoints: [
        'Use AI digital footprint, website, and registry data as primary legitimacy signals.',
        'Treat low Google ratings/reviews as a visibility gap, not automatic fraud.',
        'Open gate analysis only if other checks still conflict with declared employment.',
      ],
    };
  }

  if (ai.aiRich) {
    return {
      caseSummary: 'Some public signals exist, but not every legitimacy check passed.',
      caseSummaryPoints: [
        ...buildAiEvidenceBullets(ai),
        googleWeak
          ? 'Google Places footprint is still below pass thresholds.'
          : 'One or more legitimacy checkpoints remain in review.',
        'Compare all collected signals before deciding — no single source is decisive.',
      ],
      reviewMeasures: 'Review each legitimacy checkpoint in gate analysis.',
      reviewMeasuresPoints: [
        'Check AI footprint, website, operational status, and Maps together.',
        'Note which signal failed versus which passed.',
        'Request follow-up only where gaps remain after reviewing evidence.',
      ],
    };
  }

  return {
    caseSummary: finding.explanation,
    caseSummaryPoints: [
      'Public web and Maps signals were not strong enough to corroborate this employer.',
      googleWeak
        ? 'Google Places rating or review volume is below policy thresholds.'
        : 'Legitimacy checks flagged missing or inconclusive digital footprint.',
      'Additional evidence may be needed before confirming employment.',
    ],
    reviewMeasures: finding.recommendedAction ?? 'Review business legitimacy signals before confirming employment.',
    reviewMeasuresPoints: [
      'Open gate analysis and read each failed legitimacy checkpoint.',
      'Compare declared employer with registry, website, and Maps data.',
      'Request pay stub, offer letter, or HR confirmation if signals stay inconclusive.',
    ],
  };
}
